import Cookies from "js-cookie";
import React, { type ReactNode } from "react";

import {
  clearAuthUserSessionMirror,
  persistAuthUserSessionMirror,
  readAuthUserMerged,
} from "@/client/auth-storage";
import { config } from "@/config/config";

import AuthContext, { type IAuthenticated, TAuthPayload } from "./auth-context";

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Inicializa de forma síncrona a partir de cookie+sessionStorage para evitar
  // a race condition onde ProtectedRoute redireciona para /login antes do
  // useEffect de restauração terminar (causa o logout no refresh/navegação).
  const [auth, setAuth] = React.useState<IAuthenticated>(() => {
    const stored = readAuthUserMerged();
    if (stored.token) {
      return { ...stored, isAuthenticated: true };
    }
    return {
      isAuthenticated: false,
      id: "",
      first_name: "",
      last_name: "",
      email: "",
      token: "",
      organization_root_id: "",
      roles: undefined,
    };
  });

  const handleAuthenticate = React.useCallback(
    (userAuth: TAuthPayload) => {
      if (userAuth) {
        const user = {
          id: userAuth.id,
          first_name: userAuth.first_name,
          last_name: userAuth.last_name,
          email: userAuth.email,
          organization_root_id: userAuth.organization_root_id,
          roles: userAuth.roles,
        };
        setAuth({
          isAuthenticated: true,
          token: userAuth.token,
          ...user,
        });
        const payloadJson = JSON.stringify(userAuth);
        Cookies.set("authUser", payloadJson, {
          domain:
            config.environment === "local" ? "localhost" : ".soulhub.tec.br",
          path: "/",
          secure: config.environment !== "local",
          sameSite: "strict",
          expires: 99999999,
        });
        persistAuthUserSessionMirror(payloadJson);
        return true;
      }

      return false;
    },
    [setAuth]
  );

  const logout = () => {
    clearAuthUserSessionMirror();
    Cookies.remove("authUser", {
      path: "/",
      domain: config.environment === "local" ? "localhost" : ".soulhub.tec.br",
    });
    setAuth({
      isAuthenticated: false,
      id: "",
      token: "",
      organization_root_id: "",
      roles: undefined,
    });
  };

  return (
    <AuthContext.Provider value={{ auth, setAuth, handleAuthenticate, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
